import { inject, Injectable } from '@angular/core';
import { AanAfwezigheid } from 'src/app/afas-classes/aanafwezigheid.class';
import { Hourtype } from 'src/app/afas-classes/hourtype';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { Settlement, SettlementHourtype } from 'src/app/classes/settlement.class';
import { User } from 'src/app/classes/user.class';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { EmployeeService } from 'src/app/services/employee.service';
import {
    CONTRACT_HOURTYPE_CODE_WORK_REGULAR,
    CONTRACT_HOURTYPE_CODES,
    SettlementsService,
} from 'src/app/services/settlements.service';
import { Utils, WORK_DAY_MINUTES, WORK_WEEK_MINUTES } from 'src/app/utils.class';
import { environment } from 'src/environments/environment';
import { Week } from '../../classes/week';
import { WeekDay } from '../../classes/weekday';
import { Settleable, TimeOnDay, TimeTypeRealisationSettle } from './settleable.interface';

/**
 * Uitvoerders krijgen de uren boven de 8 gewerkte uren niet uitbetaald; die worden als
 * `UITV_MIN2` geboekt. Standaard geldt een maximum van 2 uur per dag.
 */
const MAX_UNPAID_PERFORMER_MINUTES = 120;

/**
 * Uitzondering Buitengewoon BV: voor deze medewerkers is het maximum 1 uur per dag.
 *
 * [445 Ronald Hoogenes, 413 Kees Korthof, 439 Chris Offerman, 443 Lukasz Kowalczyk,
 * 435 Hylke de Paauw, 171 Remco den Drijver, 227 Jens van Brenk, 384 Gerard Boer,
 * 309 Dennie van Minkelen]
 */
const ONE_HOUR_UNPAID_PERFORMER_IDS = [445, 413, 439, 443, 435, 171, 227, 384, 309];

const MAX_UNPAID_PERFORMER_MINUTES_EXCEPTION = 60;

@Injectable()
export class DvdsSettlementService implements Settleable {
    private __employee: Readonly<User>;

    set employee(value: Readonly<User>) {
        this.__employee = value;

        this.hasCaoBouwInfra = this.employeeService.hasCaoBouwInfra(this.__employee);
    }

    get employee(): Readonly<User> {
        return this.__employee;
    }

    private hasCaoBouwInfra = false;

    hourTypes: Hourtype[];
    realisations: Realisation[];

    updateForm: (settlements: Settlement[], day: WeekDay) => void;

    aanAfwezigheid: AanAfwezigheid[] = [];
    fcHourTypeMap = new Map<string, string>([
        ['200', 'overtime200'],
        ['200Z', 'overtime200'],
        ['200V', 'overtime200'],
        ['150', 'overtime150s'],
        ['150Z', 'overtime150s'],
        ['150V', 'overtime150s'],
        ['145', 'overtime145'],
        ['140', 'overtime140'],
        ['135', 'overtime135'],
        ['130', 'overtime130'],
        ['125', 'overtime125'],
        ['100', 'overtime100'],
        ['S100', 'overtime100'],
        ['S100W', 'overtime100'],
        ['100B', 'overtime100'],
        ['SDW', 'overtime100'],
        ['150V', 'bonus50'],
        ['145V', 'bonus45'],
        ['135V', 'bonus35'],
        ['130V', 'bonus30'],
        [SettlementHourtype.travelHours, 'traveltime'],
        [SettlementHourtype.travelHoursActual, 'traveltime'],
        ['CRW', 'travelcost'],
        ['UITV_MIN2', 'notPaidHours'],
    ]);

    readonly OVERTIME_HOURTYPE_CODES = ['200', '200Z', '150', '150Z', '145', '140', '135', '130', '125', '100', '100B'];

    private sleepOrLeaveHourCodes = new Set<string>();

    /** Zie `Settleable.dryRun`. */
    dryRun = false;

    private readonly settlementService = inject(SettlementsService);
    private readonly employeeService = inject(EmployeeService);

    constructor() {
        CONTRACT_HOURTYPE_CODES.filter(c => c !== 'MU').forEach(cht => {
            this.fcHourTypeMap.set(cht, 'normal');
        });
    }

    /**
     * Uitvoerders worden bepaald op basis van de toegekende rol, niet op het AFAS-functieveld.
     */
    private isPerformer(user: Readonly<User>) {
        return AuthorisationService.hasRole(environment.roles.Executors, user as User);
    }

    /**
     * Het maximum aan onbetaalde uren (`UITV_MIN2`) dat een uitvoerder op een dag kan krijgen.
     */
    private maxUnpaidPerformerMinutes(user: Readonly<User>) {
        return ONE_HOUR_UNPAID_PERFORMER_IDS.includes(user.id)
            ? MAX_UNPAID_PERFORMER_MINUTES_EXCEPTION
            : MAX_UNPAID_PERFORMER_MINUTES;
    }

    async settleWeekHours(week: Week) {
        week.settling = true;

        //* Each of these this.settle<Type> methods includes a saveSettlements call
        try {
            week.weekDays.forEach(d => (d.settlements = []));

            if (week.sendToAfas && !this.dryRun) {
                return;
            }

            await Promise.all(
                week.weekDays.map(day =>
                    this.settlementService.settleLeaveDays(
                        day,
                        this.aanAfwezigheid,
                        this.hourTypes,
                        this.fcHourTypeMap,
                        this.employee.id,
                        this.sleepOrLeaveHourCodes,
                        this.updateForm
                    )
                )
            );

            // Op volgorde verrekenen: de GU/MU-beslissing heeft een vaste volgorde door de week nodig, dit kan niet async
            for (const day of week.weekDays) {
                await this.settleDayHours(day, week);
            }

            if (this.hasCaoBouwInfra) {
                await this.settlementService.settleBouwCaoWeekHours(
                    week,
                    this.employee,
                    this.fcHourTypeMap,
                    this.realisations,
                    this.hourTypes,
                    this.sleepOrLeaveHourCodes,
                    this.updateForm
                );
            } else {
                await this.settlementService.settleChauffeurShortages(
                    week,
                    this.employee,
                    this.fcHourTypeMap,
                    this.hourTypes,
                    this.OVERTIME_HOURTYPE_CODES,
                    this.updateForm
                );
            }
        } finally {
            week.settling = false;
        }
    }

    private async settleDayHours(day: WeekDay, week: Week) {
        day.settling = true;

        try {
            if (day.sendToAfas && !this.dryRun) {
                return;
            }

            const { realisations, settlements } = day;

            // Slaapuren
            settlements.push(
                ...this.settlementService.settleRealisationsForHourTypes(
                    realisations,
                    [RealisationHourtype.sleep],
                    day.date.getDay() > 0 && day.date.getDay() < 6 ? 'SDU' : 'SDW',
                    this.hourTypes
                )
            );

            // Scholing
            settlements.push(
                ...this.settlementService.settleEducation(
                    settlements,
                    realisations,
                    day.date.getDay() > 0 && day.date.getDay() < 6 ? 'S100' : 'S100W',
                    day.fg.value,
                    this.hourTypes
                )
            );

            // Werk uren
            this.settleWorkingHours(day, week);

            // Pauze verrekenen
            this.settlementService.settlePauzes(day, day.fg.value.pause, this.hourTypes);

            // Cao uren; chauffeurs alleen in weekend
            const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;

            if (this.hasCaoBouwInfra || isWeekend) {
                this.settlementService.settleCaoHours(
                    day,
                    week,
                    this.realisations,
                    this.employee,
                    this.hourTypes,
                    this.sleepOrLeaveHourCodes
                );
            }

            // Verblijfskostenvergoeding
            settlements.push(
                ...this.settlementService.settleVerblijfskosten(
                    day,
                    this.employee,
                    this.hasCaoBouwInfra,
                    this.OVERTIME_HOURTYPE_CODES,
                    this.hourTypes
                )
            );

            // Reistijd
            const travelSettlements = await this.settlementService.settleTravelTime(day, this.employee, this.hourTypes);

            settlements.push(...travelSettlements);

            // Reiskosten
            const travelCostSettlements = await this.settlementService.settleTravelCosts(
                day,
                this.hasCaoBouwInfra,
                this.employee,
                this.hourTypes
            );
            settlements.push(...travelCostSettlements);

            if (!this.dryRun) {
                await this.settlementService.saveSettlements(
                    day,
                    this.employee.id,
                    this.fcHourTypeMap,
                    this.updateForm
                );
            }
        } finally {
            day.settling = false;
        }
    }

    private settleWorkingHours(day: WeekDay, week: Week) {
        const totals: TimeTypeRealisationSettle[] = [];

        day.realisations
            .filter(r =>
                [
                    RealisationHourtype.driving_to,
                    RealisationHourtype.driving_back,
                    RealisationHourtype.worktime,
                    RealisationHourtype.travel_to,
                    RealisationHourtype.travel_back,
                ].includes(r.hourtype)
            )
            .forEach(realisation => {
                this.calculateRealisationTotalHours(realisation, totals);
            });

        let pauseToSettle = day.fg.value.pause;

        const some2hourGap = this.settlementService.isGapMoreThanTwoHours(day.realisations);

        const weekDay = day.date.getDay();
        const isWeekday = weekDay > 0 && weekDay < 6;

        if (isWeekday) {
            this.handleWeekdaySettlements(
                day,
                week,
                totals.sort((a, b) => Utils.getTimeOrNull(a.to) - Utils.getTimeOrNull(b.to)),
                pauseToSettle,
                some2hourGap
            );
        } else {
            this.handleWeekendSettlements(totals, day, week);
        }
    }

    private handleWeekdaySettlements(
        day: WeekDay,
        week: Week,
        totals: TimeTypeRealisationSettle[],
        pauseToSettle: number,
        some2hourGap: boolean
    ) {
        let settledMinutes = day.settlements.map(s => s.minutes).reduce((sum, current) => sum + current, 0);

        if (this.employee.overtime) {
            settledMinutes = WORK_DAY_MINUTES;
        }

        let sleepMinutes =
            day.settlements
                .filter(s =>
                    [
                        'SDU', // Slaapdag uren
                        'SDW', // Slaapdag weekend
                    ].includes(s.hourtype_code)
                )
                .map(s => s.minutes)
                .reduce((sum, current) => sum + current, 0) ?? 0;

        settledMinutes = settledMinutes - pauseToSettle;

        for (const total of totals) {
            const tMinutes = settledMinutes + total.minutes;

            let minutesFrom12Hours = 0;

            let aboveWeekWorkHours = 0;

            let normal = total.minutes;

            let performer8to10hours = 0;

            if (tMinutes > day.fg.value.contractHours) {
                const weekWorkHours = Math.max(8 * 60 - settledMinutes, 0);

                normal = weekWorkHours;

                aboveWeekWorkHours = total.minutes - weekWorkHours;

                if (sleepMinutes && aboveWeekWorkHours > 0) {
                    const subFromAbove = Math.min(aboveWeekWorkHours, sleepMinutes);

                    sleepMinutes = subFromAbove;

                    aboveWeekWorkHours = aboveWeekWorkHours - subFromAbove;
                }

                if (this.isPerformer(this.employee) && aboveWeekWorkHours > 0 && !some2hourGap) {
                    const minutesFrom8Hours =
                        day.settlements
                            .filter(s => s.hourtype_code === 'UITV_MIN2')
                            .map(s => s.minutes)
                            .reduce((sum, current) => sum + current, 0) ?? 0;

                    const maxUnpaidMinutes = this.maxUnpaidPerformerMinutes(this.employee);

                    performer8to10hours = Math.min(maxUnpaidMinutes - minutesFrom8Hours, aboveWeekWorkHours);

                    aboveWeekWorkHours = aboveWeekWorkHours - performer8to10hours;
                }

                if (this.hasCaoBouwInfra) {
                    minutesFrom12Hours = Math.max(tMinutes - 660, 0);

                    aboveWeekWorkHours = aboveWeekWorkHours - minutesFrom12Hours;
                }
            }

            if (performer8to10hours) {
                day.settlements.push(
                    this.settlementService.createSettlementFromRealisation(
                        total.realisation,
                        this.hourTypes,
                        performer8to10hours,
                        'UITV_MIN2',
                        total.from,
                        total.to
                    )
                );
            }

            if (normal || sleepMinutes) {
                let settleMinutes = sleepMinutes > 0 ? sleepMinutes : normal;

                const hourCodeSuffix = sleepMinutes ? '' : 'V';

                let hourType = sleepMinutes ? '100B' : 'GU';

                if (total.type === TimeOnDay.Night) {
                    hourType = this.hasCaoBouwInfra ? `130${hourCodeSuffix}` : `135${hourCodeSuffix}`;

                    if (
                        !this.isPerformer(this.employee) &&
                        this.hasCaoBouwInfra &&
                        day.date.getDay() === 5 &&
                        [total.to.getDay(), total.from.getDay()].includes(5)
                    ) {
                        hourType = `150${hourCodeSuffix}`;
                    }
                }

                if (total.type === TimeOnDay.Early) {
                    hourType = this.hasCaoBouwInfra ? `130${hourCodeSuffix}` : `145${hourCodeSuffix}`;

                    if (day.date.getDay() === 5 && new Date(total.to).getDay() === 6) {
                        hourType = `150${hourCodeSuffix}`;
                    }
                }

                let settlement = null;

                const contractMinutes = this.employee.contract_hours * 60;

                const weekMinutes = week.weekDays
                    .map((d: WeekDay) =>
                        d.settlements
                            .filter(s => CONTRACT_HOURTYPE_CODES.includes(s.hourtype_code))
                            .reduce((sum, current) => sum + current.minutes, 0)
                    )
                    .reduce((sum, current) => sum + current, 0);

                const minutesInContractLeft = Math.max(0, contractMinutes - weekMinutes);

                //voor mensen die minder dan 40 uur werken maar toch meer dan contract uren werken. Rest uren wordt op MU geschreven
                if (minutesInContractLeft + pauseToSettle < settleMinutes && weekMinutes < WORK_WEEK_MINUTES) {
                    const minutesAsMu = settleMinutes - Math.min(settleMinutes, minutesInContractLeft);

                    if (minutesAsMu > 0) {
                        day.settlements.push(
                            this.settlementService.createSettlementFromRealisation(
                                total.realisation,
                                this.hourTypes,
                                minutesAsMu,
                                'MU',
                                total.from,
                                total.to
                            )
                        );

                        if (hourType !== CONTRACT_HOURTYPE_CODE_WORK_REGULAR) {
                            settlement = this.settlementService.createSettlementFromRealisation(
                                total.realisation,
                                this.hourTypes,
                                minutesAsMu,
                                hourType,
                                total.from,
                                total.to
                            );

                            day.settlements.push(settlement);
                        }
                    }

                    const otherMinutes = settleMinutes - minutesAsMu;

                    console.info(
                        `${minutesInContractLeft} min in contract over ${minutesAsMu}, min als MU overige ${otherMinutes} als GU settle mins ${settleMinutes}`
                    );

                    if (hourType === CONTRACT_HOURTYPE_CODE_WORK_REGULAR) {
                        if (otherMinutes) {
                            settlement = this.settlementService.createSettlementFromRealisation(
                                total.realisation,
                                this.hourTypes,
                                otherMinutes,
                                hourType,
                                total.from,
                                total.to
                            );

                            day.settlements.push(settlement);
                        }
                    } else {
                        settleMinutes = otherMinutes;
                    }
                } else {
                    settlement = this.settlementService.createSettlementFromRealisation(
                        total.realisation,
                        this.hourTypes,
                        settleMinutes,
                        hourType,
                        total.from,
                        total.to
                    );

                    day.settlements.push(settlement);
                }

                if (
                    hourType !== CONTRACT_HOURTYPE_CODE_WORK_REGULAR &&
                    !sleepMinutes &&
                    settlement &&
                    settleMinutes > 0
                ) {
                    const settl = this.settlementService.createSettlementFromRealisation(
                        total.realisation,
                        this.hourTypes,
                        settleMinutes,
                        'GU',
                        total.from,
                        total.to
                    );

                    settl.bonus = settlement; // even bij pause eruit filteren hierop

                    settlement.normal = settl;

                    day.settlements.push(settl);
                }
            }

            if (day.settlements.find(s => s.hourtype_code === 'F')) {
                day.settlements.push(
                    this.settlementService.createSettlementFromRealisation(
                        total.realisation,
                        this.hourTypes,
                        aboveWeekWorkHours,
                        '200',
                        total.from,
                        total.to
                    )
                );
            } else if (aboveWeekWorkHours) {
                // 6:00 - 8:00 check bij bouwInfra, dan 130 bij TimeOnDay.Normal
                let totalMinutesHigh = 0;

                if (total.type === TimeOnDay.Normal && this.hasCaoBouwInfra) {
                    const calcFrom = new Date(total.from);

                    calcFrom.setMinutes(calcFrom.getMinutes() + (normal ?? 0));

                    const highOvertimeStart = Utils.setTime(new Date(total.from), 6, 0);
                    const highOVertimeEnd = Utils.setTime(new Date(total.from), 8, 0);

                    if (
                        calcFrom.getTime() < highOVertimeEnd.getTime() &&
                        total.to.getTime() > highOvertimeStart.getTime()
                    ) {
                        const calcTo = total.to.getTime() >= highOVertimeEnd.getTime() ? highOVertimeEnd : total.to;

                        totalMinutesHigh = Utils.minuteDuration(calcTo, calcFrom);
                    }
                }

                let hourType = this.hasCaoBouwInfra ? '125' : '130';

                if (this.isPerformer(this.employee)) {
                    hourType = '100B';
                }

                if (total.type === TimeOnDay.Night) {
                    hourType = this.hasCaoBouwInfra ? '130' : '135';

                    // Als vrijdagnacht dan 150%, ook voor uitvoerders
                    if (this.hasCaoBouwInfra && total.from.getDay() === 5) {
                        hourType = '150';
                    }
                }

                if (total.type === TimeOnDay.Early) {
                    hourType = this.hasCaoBouwInfra ? (new Date(total.to).getDay() === 1 ? '200' : '130') : '145';

                    // Vrijdag op zaterdag
                    if (day.date.getDay() === 5 && new Date(total.to).getDay() === 6) {
                        hourType = '150';
                    }
                }

                if (totalMinutesHigh) {
                    let hourTypeHigh = new Date(total.to).getDay() === 1 ? '200' : '130';

                    day.settlements.push(
                        this.settlementService.createSettlementFromRealisation(
                            total.realisation,
                            this.hourTypes,
                            totalMinutesHigh,
                            hourTypeHigh,
                            total.from,
                            total.to
                        )
                    );
                }

                if (aboveWeekWorkHours - totalMinutesHigh > 0) {
                    day.settlements.push(
                        this.settlementService.createSettlementFromRealisation(
                            total.realisation,
                            this.hourTypes,
                            aboveWeekWorkHours - totalMinutesHigh,
                            hourType,
                            total.from,
                            total.to
                        )
                    );
                }
            }

            if (minutesFrom12Hours) {
                let hourType = '150';

                // Voor uitvoerder alleen vrijdag op zaterdag 150
                if (
                    this.isPerformer(this.employee) &&
                    !(total.type === TimeOnDay.Night && day.date.getDay() === 5) && // Vrijdag nacht
                    !(total.type === TimeOnDay.Early && day.date.getDay() === 5 && new Date(total.to).getDay() === 6)
                ) {
                    hourType = total.type === TimeOnDay.Normal ? '100B' : '130';
                }

                if (day.settlements.find(s => s.hourtype_code === 'F')) {
                    hourType = '200';
                }

                day.settlements.push(
                    this.settlementService.createSettlementFromRealisation(
                        total.realisation,
                        this.hourTypes,
                        minutesFrom12Hours,
                        hourType,
                        total.from,
                        total.to
                    )
                );
            }

            /**
             * Ook de onbetaalde uren meetellen: het zijn gewerkte minuten van deze dag. In de
             * praktijk verandert dit de uitkomst niet, omdat `weekWorkHours` na het eerste
             * tijdvak dat over de 8 uur gaat toch al 0 is, maar het houdt de teller kloppend met
             * wat er daadwerkelijk geboekt is.
             */
            settledMinutes += normal + aboveWeekWorkHours + performer8to10hours;
        }
    }

    private handleWeekendSettlements(totals: TimeTypeRealisationSettle[], day: WeekDay, week: Week) {
        totals.forEach(total => {
            let minutesToSettle = total.minutes;

            let hourType = total.from.getDay() === 0 ? '200Z' : '150Z';

            hourType = day.date.getDay() === 0 ? '200Z' : hourType;

            const contractMinutes = 8 * 60 * 5;

            const weekMinutes = week.weekDays
                .map(d =>
                    d.settlements
                        .filter(s => CONTRACT_HOURTYPE_CODES.includes(s.hourtype_code))
                        .reduce((sum, current) => sum + current.minutes, 0)
                )
                .reduce((sum, current) => sum + current, 0);

            const minInContractLeft = Math.max(0, contractMinutes - weekMinutes);

            if (minInContractLeft > 0 && !this.employee.overtime) {
                const minutesAsMu = Math.min(total.minutes, minInContractLeft);

                const muSettle = this.settlementService.createSettlementFromRealisation(
                    total.realisation,
                    this.hourTypes,
                    minutesAsMu,
                    'MU',
                    total.from,
                    total.to
                );

                const bonusSettle = this.settlementService.createSettlementFromRealisation(
                    total.realisation,
                    this.hourTypes,
                    minutesAsMu,
                    hourType.replace('Z', 'V'),
                    total.from,
                    total.to
                );

                muSettle.bonus = bonusSettle; // Op basis hiervan wordt er bij pause bepaalt dat dit bonus is.

                bonusSettle.normal = muSettle;

                day.settlements.push(muSettle);
                day.settlements.push(bonusSettle);

                minutesToSettle = minutesToSettle - minutesAsMu;
            }

            if (minutesToSettle) {
                day.settlements.push(
                    this.settlementService.createSettlementFromRealisation(
                        total.realisation,
                        this.hourTypes,
                        minutesToSettle,
                        hourType,
                        total.from,
                        total.to
                    )
                );
            }
        });
    }

    private calculateRealisationTotalHours(realisation: Realisation, totals: TimeTypeRealisationSettle[]) {
        const beginDate = new Date(realisation.begindate);
        const endDate = new Date(realisation.enddate);

        const startEarly = Utils.setTime(new Date(endDate), 0, 0);
        const endEarly = Utils.setTime(new Date(endDate), 6, 0);
        const startNight = Utils.setTime(new Date(beginDate), this.hasCaoBouwInfra ? 20 : 18, 0);
        const midNight = Utils.setTime(new Date(beginDate), 24, 0);

        let nightMinutes = 0;
        let earlyMinutes = 0;

        // nacht
        if (beginDate.getTime() < midNight.getTime() && endDate.getTime() > startNight.getTime()) {
            const calcUntil = endDate.getTime() > midNight.getTime() ? midNight : endDate;
            const calcFrom = beginDate.getTime() < startNight.getTime() ? startNight : beginDate;
            const minutes = Utils.minuteDuration(calcUntil, calcFrom);

            totals.push({
                from: calcFrom,
                to: calcUntil,
                minutes,
                type: TimeOnDay.Night,
                realisation,
            });

            nightMinutes += minutes;
        }

        // ochtend
        if (beginDate.getTime() < endEarly.getTime() && endDate.getTime() > startEarly.getTime()) {
            const calcFrom = beginDate.getTime() < startEarly.getTime() ? startEarly : beginDate;
            const calcUntil = endDate.getTime() > endEarly.getTime() ? endEarly : endDate;
            const minutes = Utils.minuteDuration(calcUntil, calcFrom);

            totals.push({
                from: calcFrom,
                to: calcUntil,
                minutes,
                type: TimeOnDay.Early,
                realisation,
            });

            earlyMinutes += minutes;
        }

        // normaal
        const minutesNormal = Utils.minuteDuration(endDate, beginDate) - (nightMinutes + earlyMinutes);

        if (minutesNormal) {
            const from = new Date(beginDate);
            const to = new Date(endDate);

            let minutesNormalFirst = 0;

            if (from.getTime() < startEarly.getTime() && from.getTime() < startNight.getTime()) {
                const normalFromFirst = new Date(from);
                const normalToFirst = new Date(Math.min(startNight.getTime(), startEarly.getTime()));
                const minutes = Utils.minuteDuration(normalToFirst, normalFromFirst);

                totals.push({
                    from: normalFromFirst,
                    to: normalToFirst,
                    minutes,
                    type: TimeOnDay.Normal,
                    realisation,
                });

                minutesNormalFirst += minutes;
            }

            if (minutesNormalFirst) {
                from.setMinutes(from.getMinutes() + (nightMinutes + earlyMinutes));
            } else {
                from.setMinutes(from.getMinutes() + earlyMinutes);
                to.setMinutes(to.getMinutes() - nightMinutes);
            }

            if (minutesNormal - minutesNormalFirst > 0) {
                const minutes = minutesNormal - minutesNormalFirst;

                let fromNormalSec = new Date(from);

                if (minutesNormalFirst) {
                    fromNormalSec = new Date(to);
                    fromNormalSec.setMinutes(fromNormalSec.getMinutes() - minutes);
                }

                totals.push({
                    from: fromNormalSec,
                    to,
                    minutes,
                    type: TimeOnDay.Normal,
                    realisation,
                });
            }
        }
    }
}
